import { Band, CtaArrow, ctaClasses, Medallion, Pill, BACK_LINK } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { Container, PageHero } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { CATEGORY_MARK } from './category-mark';
import { DecisionSplit } from './DecisionSplit';
import { FullReview } from './FullReview';
import { GlanceTable } from './GlanceTable';

import type { Comparison } from '@/features/comparisons';
import type { ReactElement } from 'react';

/**
 * AF-136 — the `/vs/<rival>` comparison page.
 *
 * Composition only: the hero, the three sections, the closing band. Each
 * section owns its own design and its own reasoning — `DecisionSplit`,
 * `GlanceTable`, `FullReview`. They were extracted when this file crossed the
 * 300-line ceiling during the design pass, and the split is along the seams the
 * page already had rather than at a line count.
 *
 * Owns the page's SEO head (title + description + canonical + OG/Twitter/
 * JSON-LD) since it always has a resolved comparison — `VsPage` only routes;
 * the 404 branch sets its own title.
 *
 * ## The page has tonal rhythm now, and that is the point of the bands
 *
 * It was one continuous white sheet: hero, cards, table, ~2,000 words of grey
 * prose, two buttons. Nothing told the eye where one idea ended and the next
 * began, so a page carrying real research read as a page carrying none — which
 * is the specific failure this pass was opened to fix.
 *
 * Three grounds now alternate down the page: the decision + table on `page`,
 * the long review on `sunk`, the close on the dark `band`. The dark close is
 * not a new invention — `/vs`, `/features` and `/one-pagers` all end on it, and
 * this page ending on two bare buttons made it the odd one out of its own
 * family.
 */

/**
 * `intercom-fin` → `intercomFin`. The catalog namespaces are camelCase
 * throughout (`vs.intercomFin.tldr`), so the review keys follow rather than
 * introducing a second spelling of the same ids.
 */
const reviewSlug = (slug: string): string =>
  slug.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());

export function ComparisonPage({ data }: { readonly data: Comparison }): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('vs.detail.metaTitle', { rival: data.rivalName }),
    description: t(data.summaryKey),
    path: `/vs/${data.slug}`,
  });

  const mark = CATEGORY_MARK[data.category];

  return (
    <main id="main" tabIndex={-1} data-syncanix-vs={data.slug}>
      <PageHero
        align="start"
        width="wide"
        eyebrow={
          // The back link and the category, on one line. The category is the
          // same mark the index card carried, so the click the reader just made
          // is still visible to them — a detail page that drops its badge reads
          // as a different page about the same company.
          <span className="flex flex-wrap items-center gap-x-4 gap-y-3">
            <Link className={BACK_LINK} to="/vs">
              {t('vs.detail.backToAll')}
            </Link>
            <span className="inline-flex items-center gap-2">
              <Medallion icon={mark.icon} hue={mark.hue} size="sm" />
              <Pill>{t(`vs.category.${data.category}`)}</Pill>
            </span>
          </span>
        }
        title={t('vs.detail.heading', { rival: data.rivalName })}
        description={t(data.tldrKey)}
      />

      <Container width="wide" className="pb-16 pt-12 sm:pb-20 sm:pt-14">
        {/* The decision rule, above the table rather than buried at the end of
            the Tldr paragraph. These two sentences ARE the thing a reader came
            for — "so which do I pick?" — and they used to be the last two
            sentences of a 90-word block. The same defect was found and fixed on
            the index CARD first; this is that fix applied to the page where the
            decision is actually made. */}
        <DecisionSplit data={data} />

        <section aria-labelledby="at-a-glance" className="mt-16">
          <h2
            id="at-a-glance"
            className="text-[clamp(1.5rem,2.6vw,2rem)] font-semibold leading-[1.1] tracking-[-0.036em] text-[color:var(--bd-ink)]"
          >
            {t('vs.detail.atAGlance')}
          </h2>

          <GlanceTable data={data} />

          {/* Freshness disclosure — matches the build-vs-buy footnote tone. */}
          <p data-freshness-note className="mt-4 text-xs text-[color:var(--bd-faint)]">
            {t('vs.freshnessNote')}
          </p>
        </section>
      </Container>

      {/* The long-form review, on the sunk ground. The table above answers
          "which one"; this answers "and why, in enough detail that I can repeat
          it to someone else". See `FullReview` for why the rival's case is put
          first and why it is open by default. */}
      <Band as="section" tone="sunk" space="sm" aria-labelledby="full-review-heading">
        <FullReview slug={reviewSlug(data.slug)} rivalName={data.rivalName} />
      </Band>

      <Band as="section" tone="band" space="sm" className="text-center" aria-labelledby="vs-close">
        <h2
          id="vs-close"
          className="mx-auto max-w-2xl text-[clamp(1.4rem,2.6vw,1.95rem)] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-band-ink)]"
        >
          {t('vs.detail.closeHeading', { rival: data.rivalName })}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-pretty text-[0.95rem] leading-[1.66] text-[color:var(--bd-band-body)]">
          {t('vs.detail.closeBody')}
        </p>
        {/* `ctaClasses` spread onto a router `<Link>` rather than `<CtaLink>`:
            these are in-SPA routes, and `CtaLink` renders a plain `<a>` (it is
            for cross-origin destinations like the app login). */}
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/pricing" className={ctaClasses({ onDark: true })}>
            {t('vs.detail.seePricing')}
            <CtaArrow />
          </Link>
          <Link to="/how-it-works" className={ctaClasses({ variant: 'secondary', onDark: true })}>
            {t('vs.detail.howItWorks')}
          </Link>
        </div>
      </Band>
    </main>
  );
}
